import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { seoService } from '../../services/seoService';
import type { SEOSettings } from '../../types';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  page?: 'home' | 'tours' | 'blog' | 'about' | 'contact';
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  page
}) => {
  const [seoSettings, setSeoSettings] = useState<SEOSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSEOSettings = async () => {
      try {
        const settings = await seoService.getSEOSettings();
        setSeoSettings(settings);
      } catch (error) {
        console.error('SEO ayarları yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSEOSettings();
  }, []);

  if (loading || !seoSettings) {
    return (
      <Helmet>
        <title>HatTravel</title>
        <meta name="description" content="HatTravel - Seyahat ve Turizm" />
      </Helmet>
    );
  }

  // Sayfa özel ayarlarını al
  const pageSettings = page ? seoSettings.pageSettings[page] : null;
  
  // Başlık oluştur
  const finalTitle = title || 
    (pageSettings?.title ? seoSettings.titleTemplate.replace('%s', pageSettings.title) : seoSettings.defaultTitle);
  
  // Açıklama oluştur
  const finalDescription = description || 
    (pageSettings?.description || seoSettings.defaultMetaDescription);
  
  // Anahtar kelimeler oluştur
  const finalKeywords = keywords || 
    (pageSettings?.keywords || seoSettings.defaultMetaKeywords);
  
  // Resim URL'si
  const finalImage = image || 
    seoSettings.openGraph?.defaultImage || 
    seoSettings.twitterCard?.defaultImage || 
    '/images/default-og-image.jpg';
  
  // URL
  const finalUrl = url || `${seoSettings.siteUrl}${window.location.pathname}`;

  return (
    <Helmet>
      {/* Temel Meta Etiketleri */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords.join(', ')} />
      <meta name="author" content={seoSettings.siteName} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={finalUrl} />
      
      {/* Open Graph Meta Etiketleri */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content={seoSettings.openGraph?.siteName || seoSettings.siteName} />
      <meta property="og:locale" content={seoSettings.openGraph?.locale || 'tr_TR'} />
      
      {/* Twitter Card Meta Etiketleri */}
      <meta name="twitter:card" content={seoSettings.twitterCard?.cardType || 'summary_large_image'} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      {seoSettings.twitterCard?.site && (
        <meta name="twitter:site" content={seoSettings.twitterCard.site} />
      )}
      {seoSettings.twitterCard?.creator && (
        <meta name="twitter:creator" content={seoSettings.twitterCard.creator} />
      )}
      
      {/* Schema.org Yapılandırılmış Veri */}
      {seoSettings.schema?.enableOrganizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": seoSettings.schema.organizationName,
            "url": seoSettings.siteUrl,
            "logo": seoSettings.schema.organizationLogo,
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": seoSettings.schema.organizationPhone,
              "contactType": "customer service",
              "email": seoSettings.schema.organizationEmail
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": seoSettings.schema.organizationAddress
            }
          })}
        </script>
      )}
      
      {/* Analytics Kodları */}
      {seoSettings.analytics?.googleAnalyticsId && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${seoSettings.analytics.googleAnalyticsId}`}></script>
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${seoSettings.analytics.googleAnalyticsId}');
            `}
          </script>
        </>
      )}
      
      {seoSettings.analytics?.googleTagManagerId && (
        <>
          <script>
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${seoSettings.analytics.googleTagManagerId}');
            `}
          </script>
        </>
      )}
      
      {seoSettings.analytics?.facebookPixelId && (
        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${seoSettings.analytics.facebookPixelId}');
            fbq('track', 'PageView');
          `}
        </script>
      )}
      
      {/* Özel Analytics Kodu */}
      {seoSettings.analytics?.customAnalyticsCode && (
        <script dangerouslySetInnerHTML={{ __html: seoSettings.analytics.customAnalyticsCode }} />
      )}
      
      {/* Hreflang Etiketleri */}
      {seoSettings.hreflangTags.map((tag, index) => (
        <link key={index} rel="alternate" hrefLang={tag.lang} href={tag.url} />
      ))}
    </Helmet>
  );
};

export default SEOHead; 